import { Button } from "antd";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";
import { CONTACT_PATH } from "../../../constants/company.constants";
import cn from "../../../lib/cn";

/* Mini UI previews for each onboarding step */

const PlanPreview = () => (
  <div className="flex gap-1.5">
    {["Basic", "Standard", "Premium"].map((plan) => {
      const selected = plan === "Standard";
      return (
        <span
          key={plan}
          className={cn(
            "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium",
            selected
              ? "border-theme-primary bg-theme-primary/10 text-theme-primary"
              : "border-gray-200 bg-white text-gray-400",
          )}
        >
          {selected && <Check className="h-3 w-3" strokeWidth={3} />}
          {plan}
        </span>
      );
    })}
  </div>
);

const OtpPreview = () => (
  <div className="flex gap-1.5">
    {["4", "8", "2", "6", "", ""].map((digit, i) => (
      <span
        key={i}
        className={cn(
          "flex h-8 w-7 items-center justify-center rounded-md border bg-white text-sm font-semibold text-gray-900",
          i === 4
            ? "border-theme-primary ring-2 ring-theme-primary/15"
            : "border-gray-200",
        )}
      >
        {digit}
        {i === 4 && (
          <span className="h-3.5 w-px animate-pulse bg-theme-primary" />
        )}
      </span>
    ))}
  </div>
);

const SetupPreview = () => (
  <div className="flex flex-wrap gap-1.5">
    {[
      { label: "Branches", done: true },
      { label: "Departments", done: true },
      { label: "Employees", done: false },
    ].map((item) => (
      <span
        key={item.label}
        className={cn(
          "inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium",
          item.done
            ? "bg-emerald-50 text-emerald-600"
            : "bg-white text-gray-400 ring-1 ring-inset ring-gray-200",
        )}
      >
        {item.done && <Check className="h-3 w-3" strokeWidth={3} />}
        {item.label}
      </span>
    ))}
  </div>
);

const STEPS: { title: string; description: string; preview: ReactNode }[] = [
  {
    title: "Choose your plan",
    description: "Pick the plan that fits your team size and modules.",
    preview: <PlanPreview />,
  },
  {
    title: "Verify your email",
    description: "Confirm with a one-time code to create your account.",
    preview: <OtpPreview />,
  },
  {
    title: "Set up your company",
    description: "Add branches, departments and employees — and go live.",
    preview: <SetupPreview />,
  },
];

// Used by the "Start free trial" button (paused).
// const scrollToPricing = () =>
//   document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });

const GetStarted = () => {
  return (
    <section className="bg-white px-4 pb-24 pt-8 sm:px-6 sm:pb-28 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        className="relative mx-auto max-w-7xl overflow-hidden rounded-3xl border border-gray-200 bg-gray-50"
      >
        {/* Subtle dotted texture + glow */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(rgba(15,23,42,0.07)_1px,transparent_1px)] [background-size:20px_20px] [mask-image:linear-gradient(to_right,transparent,black_60%)]" />
        <div className="pointer-events-none absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-theme-primary/15 blur-[100px]" />

        <div className="relative grid gap-12 p-8 sm:p-12 lg:grid-cols-2 lg:gap-16 lg:p-16">
          {/* Copy + CTAs */}
          <div className="flex flex-col justify-center">
            <p className="text-sm font-semibold uppercase tracking-wider text-theme-primary">
              Get started
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Up and running in three steps
            </h2>
            <p className="mt-4 max-w-md text-lg leading-relaxed text-gray-500">
              {/* Free trial paused — original copy:
                  "Self-serve onboarding with a 7-day free trial on every plan." */}
              Sign up and set up your company yourself. Prefer a guided setup?
              Our team will walk you through it.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {/* Free trial paused — restore this button when trials are back.
              <Button
                size="large"
                type="primary"
                onClick={scrollToPricing}
                className="btn-primary h-12 px-7 font-medium"
              >
                Start free trial
                <ArrowRight className="h-4 w-4" />
              </Button>
              */}
              <Link to={CONTACT_PATH}>
                <Button
                  size="large"
                  type="primary"
                  className="btn-primary h-12 w-full px-7 font-medium"
                >
                  Request a Demo
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Steps */}
          <ol className="relative space-y-3">
            <div className="pointer-events-none absolute bottom-10 left-[31px] top-10 sm:left-[35px] w-px bg-gradient-to-b from-theme-primary/50 via-gray-200 to-gray-200" />
            {STEPS.map((step, index) => (
              <motion.li
                key={step.title}
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{
                  duration: 0.5,
                  delay: 0.15 + index * 0.1,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="relative flex gap-4 rounded-2xl border border-gray-200 bg-white p-4 sm:p-5"
              >
                <span className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-theme-primary text-xs font-semibold text-white ring-4 ring-white">
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900">{step.title}</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {step.description}
                  </p>
                  <div className="mt-3">{step.preview}</div>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </motion.div>
    </section>
  );
};

export default GetStarted;
